import { Bell, RefreshCw, Calendar } from "lucide-react";
import ResourcePersonNavBar from "../components/ResourcePersonNavBar";
import useResourcePersonNotifications from "../hooks/useResourcePersonNotifications";
import { useResourcePersonDashboard } from "../store/resourcePerson/resourcePersonStore";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

/**
 * Resource-person notifications.
 *
 * The resource person API has no notifications endpoint, so this page uses the
 * account's real recent `activity` from GET /api/resource-person/dashboard.
 */
export default function Notifications() {
  const { notifications, loading } = useResourcePersonNotifications();
  const { fetchDashboard, dashboardLoading } = useResourcePersonDashboard();

  return (
    <div className="min-h-screen bg-slate-50">
      <ResourcePersonNavBar title="Notifications" subTitle="Activity" />
      <main className="mx-auto max-w-4xl px-4 py-6 md:px-6">
        <section className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div>
            <h1 className="m-0 text-2xl font-bold text-slate-900">Notifications</h1>
            <p className="m-0 mt-1 text-sm text-slate-500">
              Recent activity on your resource-person account.
            </p>
          </div>
          <button
            type="button"
            onClick={() => fetchDashboard()}
            disabled={dashboardLoading}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${dashboardLoading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </section>

        <section className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-sm text-slate-500">
              Loading notifications…
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                <Bell className="h-7 w-7" />
              </span>
              <p className="m-0 mt-4 text-sm font-semibold text-slate-700">No notifications yet</p>
              <p className="m-0 mt-1 max-w-sm text-sm text-slate-500">
                Activity such as profile updates, submissions and moderation decisions will appear
                here.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {notifications.map((item) => (
                <li key={item.id} className="flex items-start gap-4 px-5 py-4">
                  <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                    <Bell className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="m-0 text-sm font-semibold text-slate-900">{item.title}</p>
                    <p className="m-0 mt-0.5 text-sm text-slate-500">{item.description}</p>
                    <p className="m-0 mt-1 flex items-center gap-1.5 text-xs text-slate-400">
                      <Calendar className="h-3.5 w-3.5" />
                      {formatDate(item.createdAt)}
                      {item.time ? ` · ${item.time}` : ""}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
